import type { KeyValueStorage } from '../game/save';
import { clamp } from '../utils/math';
import { stripControlChars } from '../utils/text';

export type Quality = 'low' | 'high';

export interface Settings {
  playerName: string;
  playerColor: string;
  musicVolume: number;
  sfxVolume: number;
  muted: boolean;
  quality: Quality;
}

export const PLAYER_COLORS = [
  '#ff8fab',
  '#7cc6fe',
  '#9be38a',
  '#ffc94d',
  '#b79cff',
  '#ff9f68',
] as const;

export const MAX_PLAYER_NAME_LENGTH = 16;

const SETTINGS_KEY = 'petal-plains:settings';

const CUTE_NAMES = ['Sprout', 'Pip', 'Bramble', 'Clover', 'Juniper', 'Poppy', 'Fern', 'Bean'];

export function sanitizePlayerName(name: string): string {
  return stripControlChars(name).replace(/[<>]/g, '').trim().slice(0, MAX_PLAYER_NAME_LENGTH);
}

function defaultSettings(): Settings {
  const name = CUTE_NAMES[Math.floor(Math.random() * CUTE_NAMES.length)] ?? 'Sprout';
  const prefersLow =
    typeof navigator !== 'undefined' &&
    (/Mobi|Android/i.test(navigator.userAgent) || (navigator.hardwareConcurrency ?? 8) <= 4);
  return {
    playerName: `${name}${Math.floor(Math.random() * 90 + 10)}`,
    playerColor: PLAYER_COLORS[Math.floor(Math.random() * PLAYER_COLORS.length)] ?? '#ff8fab',
    musicVolume: 0.5,
    sfxVolume: 0.7,
    muted: false,
    quality: prefersLow ? 'low' : 'high',
  };
}

export function loadSettings(storage: KeyValueStorage): Settings {
  const defaults = defaultSettings();
  let stored: string | null = null;
  try {
    stored = storage.getItem(SETTINGS_KEY);
  } catch {
    /* storage unavailable */
  }
  if (stored === null) {
    // First visit: remember the generated name/colour so they stay stable.
    saveSettings(storage, defaults);
    return defaults;
  }
  try {
    const raw = JSON.parse(stored) as Partial<Settings>;
    return {
      playerName: sanitizePlayerName(String(raw.playerName ?? '')) || defaults.playerName,
      playerColor: (PLAYER_COLORS as readonly string[]).includes(raw.playerColor ?? '')
        ? (raw.playerColor as string)
        : defaults.playerColor,
      musicVolume: clamp(Number(raw.musicVolume ?? defaults.musicVolume) || 0, 0, 1),
      sfxVolume: clamp(Number(raw.sfxVolume ?? defaults.sfxVolume) || 0, 0, 1),
      muted: raw.muted === true,
      quality: raw.quality === 'low' || raw.quality === 'high' ? raw.quality : defaults.quality,
    };
  } catch {
    return defaults;
  }
}

export function saveSettings(storage: KeyValueStorage, settings: Settings): void {
  try {
    storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* storage unavailable — settings just won't persist */
  }
}
